import { Link } from 'react-router-dom';
import { ArrowUpRight, Newspaper } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { useNoticiasRelacionadas } from '@/hooks/useNoticias';

interface NoticiasRelacionadasCardProps {
  empresaId: number;
}

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Espelha o visual do IntelligenceQuickAsk: ambos ocupam uma célula do grid de
 * dois cards do Meu Feed, por isso a section raiz usa h-full.
 */
export function NoticiasRelacionadasCard({ empresaId }: NoticiasRelacionadasCardProps) {
  const { data, isLoading } = useNoticiasRelacionadas(empresaId);

  return (
    <section
      className="flex h-full flex-col rounded-xl border border-ds-brand/30 bg-ds-brand-subtle/30 p-4 shadow-sm"
      aria-label="Notícias do seu nicho"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ds-brand/10 p-2 text-ds-brand" aria-hidden="true">
          <Newspaper className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-ds-text-primary">Notícias do seu nicho</h2>
            <span className="inline-flex items-center gap-1 rounded-full bg-ds-brand/10 px-2 py-1 text-xs font-medium text-ds-brand">
              Termos do funil da sua empresa
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 min-h-0 flex-1">
        {isLoading && (
          <div className="flex items-center justify-center py-8" role="status" aria-label="Carregando notícias">
            <div className="h-6 w-6 rounded-full border-2 border-ds-border border-t-ds-brand animate-spin" />
          </div>
        )}

        {!isLoading && data && !data.configurado && (
          <p className="text-sm text-ds-text-secondary">
            Configure os termos do funil para ver notícias coerentes.{' '}
            <Link to="/noticias" className="font-medium text-ds-brand hover:underline">
              Configurar em Notícias
            </Link>
          </p>
        )}

        {!isLoading && data?.configurado && data.noticias.length === 0 && (
          <p className="text-sm text-ds-text-secondary">
            Nenhuma notícia coerente com o perfil no momento.
          </p>
        )}

        {!isLoading && data && data.noticias.length > 0 && (
          // Altura máxima fixa com scroll: o card mantém o tamanho do irmão
          // ("Pergunte à Inteligência") independente da quantidade de notícias.
          <ul className="max-h-[240px] space-y-3 overflow-y-auto pr-1">
            {data.noticias.map((noticia) => (
              <li key={noticia.id} className="rounded-lg border border-ds-brand/20 bg-ds-surface p-3">
                <div className="flex items-start justify-between gap-3">
                  {noticia.url ? (
                    <a
                      href={noticia.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="line-clamp-2 min-w-0 flex-1 text-sm font-medium text-ds-text-primary hover:text-ds-brand"
                    >
                      {noticia.titulo}
                      <ArrowUpRight className="ml-1 inline h-3.5 w-3.5 align-text-top text-ds-text-secondary" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="line-clamp-2 min-w-0 flex-1 text-sm font-medium text-ds-text-primary">{noticia.titulo}</span>
                  )}
                  <Badge variant="info" size="sm" className="shrink-0">
                    {noticia.score}%
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-ds-text-secondary">
                  {noticia.fonte || 'Fonte não informada'}
                  {noticia.dataPublicacao ? ` • ${formatDate(noticia.dataPublicacao)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3 border-t border-ds-brand/20 pt-3">
        <Link to="/noticias" className="text-xs font-medium text-ds-brand hover:underline">
          Ver todas em Notícias
        </Link>
      </div>
    </section>
  );
}
